import { Box, Chip, Divider, Link, Typography } from '@mui/material'
import { ITask } from '../reducers'
import TaskActions from './TaskActions'

/**
 * Panneau déployé d'une tâche.
 *
 * La description est rendue en `pre-line` : `addTask` concatène les occurrences
 * successives avec un `\n` quand leur texte diffère, et une tâche peut porter
 * des dizaines de lignes. Un `noWrap` masquerait l'essentiel de l'information.
 */
export const TaskDetailPanel = ({
  row,
  onTransition,
  onComment,
}: {
  row: ITask
  onTransition: (
    id: string,
    status: 'DONE' | 'REJECTED',
    comment: string | undefined,
  ) => Promise<unknown>
  onComment: (id: string, comment: string) => Promise<unknown>
}) => (
  <Box sx={{ p: 2, background: '#fff' }}>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {row.name}
      </Typography>
      {row.priority === 'URGENT' && (
        <Chip label="Urgent" color="error" size="small" />
      )}
      {row.causeStillHolds === false && (
        <Chip label="Cause levée" color="success" size="small" />
      )}
      {row.causeStillHolds === null && (
        <Chip label="Non vérifiable" size="small" variant="outlined" />
      )}
    </Box>

    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
      Ouverte le {new Date(row.createdAt).toLocaleDateString('fr-FR')} — il y a{' '}
      {row.ageInDays} jour{row.ageInDays > 1 ? 's' : ''}
      {row.companyName ? ` — ${row.companyName}` : ''}
    </Typography>

    <Divider sx={{ my: 1 }} />

    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
      {row.description}
    </Typography>

    <Divider sx={{ my: 1 }} />

    <Typography variant="body2">
      Référence :{' '}
      {row.resolvedReference.link ? (
        <Link href={row.resolvedReference.link}>
          {row.resolvedReference.label}
        </Link>
      ) : (
        <span>{row.resolvedReference.label}</span>
      )}
    </Typography>

    {/*
      « Cause levée » recouvre deux situations qui ne se disent pas pareil : le
      problème a disparu, ou il est intact et on a décidé de ne plus le
      signaler. `causeLiftedReason` fait la différence, calculée par l'API.
    */}
    {row.causeLiftedReason === 'OUT_OF_SCOPE' && (
      <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
        Cette référence a été déclarée hors périmètre de facturation. Elle n’est
        plus signalée : cette tâche peut être refermée sans action.
      </Typography>
    )}

    {row.causeStillHolds === false &&
      row.causeLiftedReason !== 'OUT_OF_SCOPE' && (
        <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
          Le problème signalé n’existe plus dans les données. Cette tâche peut
          être refermée sans action.
        </Typography>
      )}

    {!row.isOpen && (
      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
        Tâche fermée{row.currentStatus ? ` — ${row.currentStatus}` : ''}.{' '}
        {row.causeLiftedReason === 'OUT_OF_SCOPE'
          ? 'La référence étant hors périmètre, elle ne sera pas recréée.'
          : 'Si le problème n’a pas été corrigé, elle sera recréée au prochain cycle de transmission.'}
      </Typography>
    )}

    <TaskActions
      task={row}
      onTransition={onTransition}
      onComment={onComment}
    />
  </Box>
)

export default TaskDetailPanel
